import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Trash2, Edit, Plus, X } from 'lucide-react';

// URL DE PRODUCCIÓN EN RENDER
const API_BASE_URL = 'https://voltaris-api.onrender.com/api';

const AdminUsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  
  const [currentUser, setCurrentUser] = useState({ 
    id: null, username: '', password: '', role: 'viewer', permissions: {} 
  });

  const fetchUsers = async () => {
    try {
      const session = JSON.parse(localStorage.getItem('voltaris_user'));
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: { 'Authorization': `Bearer ${session?.token}` }
      });
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) { toast.error('Error al cargar usuarios'); } 
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);

    if (password.length < minLength) return "Mínimo 8 caracteres.";
    if (!hasUpperCase || !hasLowerCase || !hasNumbers) return "Debe incluir mayúsculas, minúsculas y números.";
    return null;
  };

  const handleSaveUser = async () => {
    if (modalMode === 'create' && users.find(u => u.username.toLowerCase() === currentUser.username.toLowerCase())) {
        toast.error("El nombre de usuario ya existe.");
        return;
    }

    const passError = validatePassword(currentUser.password);
    if (passError) {
        toast.error(passError);
        return;
    }

    const session = JSON.parse(localStorage.getItem('voltaris_user'));
    const url = modalMode === 'create' ? `${API_BASE_URL}/users` : `${API_BASE_URL}/users/${currentUser.id}`;
    const method = modalMode === 'create' ? 'POST' : 'PUT';

    try {
      const response = await fetch(url, {
        method: method,
        headers: { 
          'Authorization': `Bearer ${session?.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            ...currentUser,
            permissions: currentUser.permissions || {}
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        toast.error("Error al guardar: " + (data.detail || "Error desconocido"));
        return;
      }
      
      toast.success(modalMode === 'create' ? 'Usuario creado' : 'Usuario actualizado');
      setIsModalOpen(false);
      fetchUsers();
    } catch (error) { toast.error('Error de conexión'); }
  };

  const deleteUser = async (id) => {
    if (!confirm('¿Seguro que quieres borrar este usuario?')) return;
    const session = JSON.parse(localStorage.getItem('voltaris_user'));
    await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${session?.token}` }
    });
    toast.success('Usuario eliminado');
    fetchUsers();
  };

  const openModal = (mode, user = { id: null, username: '', password: '', role: 'viewer', permissions: {} }) => {
    setModalMode(mode);
    setCurrentUser(user);
    setIsModalOpen(true);
  };

  if (loading) return <div className="p-20 text-center">Cargando gestión...</div>;

  return (
    <div className="max-w-6xl mx-auto p-8 pt-28">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Gestión de Usuarios</h1>
        <button onClick={() => openModal('create')} className="bg-primary text-white px-4 py-2 rounded flex items-center gap-2">
          <Plus size={18} /> Crear Usuario
        </button>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96 relative">
            <button 
                onClick={() => setIsModalOpen(false)} 
                className="absolute top-4 right-4 text-gray-500 hover:text-black"
            >
                <X size={24} />
            </button>

            <h2 className="text-xl font-bold mb-4">{modalMode === 'create' ? 'Crear Usuario' : 'Editar Usuario'}</h2>
            <input className="w-full border p-2 mb-2 rounded" placeholder="Usuario" value={currentUser.username} onChange={e => setCurrentUser({...currentUser, username: e.target.value})} />
            <input className="w-full border p-2 mb-4 rounded" type="password" placeholder="Contraseña (mín 8, may/min/num)" onChange={e => setCurrentUser({...currentUser, password: e.target.value})} />
            <select className="w-full border p-2 mb-6 rounded" value={currentUser.role} onChange={e => setCurrentUser({...currentUser, role: e.target.value})}>
              <option value="viewer">Solo Lectura</option>
              <option value="editor">Editor Portfolio</option>
              <option value="admin">Super Admin</option>
            </select>
            <button onClick={handleSaveUser} className="w-full py-2 bg-primary text-white rounded font-bold">Guardar Cambios</button>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b">
            <tr><th className="p-4">Nombre</th><th className="p-4">Rol</th><th className="p-4 text-right">Acciones</th></tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b">
                <td className="p-4">{user.username}</td>
                <td className="p-4 capitalize">{user.role}</td>
                <td className="p-4 flex justify-end gap-2">
                  <button onClick={() => openModal('edit', user)} className="text-blue-600"><Edit size={18} /></button>
                  <button onClick={() => deleteUser(user.id)} className="text-red-600"><Trash2 size={18} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminUsersPanel;